'use client';

import { useState } from 'react';
import { useFormatter, useTranslations } from 'next-intl';
import { KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useLicenseStatus } from '../hooks/use-license-status';
import { useActivateLicense } from '../hooks/use-activate-license';

/**
 * Current license status plus a field to paste a signed license key.
 * Shared by the Settings → License card (Super Admin) and the public
 * license-expired page, so renewal works with or without a session.
 */
export function LicenseKeyForm() {
  const t = useTranslations('license');
  const format = useFormatter();
  const { data } = useLicenseStatus();
  const activate = useActivateLicense();
  const [key, setKey] = useState('');

  const expiresLabel = data?.expires_at
    ? format.dateTime(new Date(data.expires_at), { dateStyle: 'long' })
    : null;

  async function handleActivate() {
    const trimmed = key.trim();
    if (!trimmed) return;
    try {
      await activate.mutateAsync(trimmed);
      setKey('');
      toast.success(t('activateSuccess'));
    } catch {
      // Global mutation error toast surfaces the invalid-key message.
    }
  }

  return (
    <div className='space-y-4'>
      <div className='text-sm'>
        <span className='text-muted-foreground'>{t('currentStatus')}: </span>
        {data?.expired ? (
          <span className='text-destructive font-medium'>
            {t('expiredTag')}
          </span>
        ) : expiresLabel ? (
          <span className='font-medium'>
            {t('licensedUntil', { date: expiresLabel })}
          </span>
        ) : data?.has_key ? (
          <span className='font-medium'>{t('perpetual')}</span>
        ) : (
          <span className='font-medium'>{t('unlimited')}</span>
        )}
        {data?.domain ? (
          <span className='text-muted-foreground'>
            {' '}
            · {t('boundTo', { domain: data.domain })}
          </span>
        ) : null}
      </div>

      <div className='space-y-1.5'>
        <Label htmlFor='license_key'>{t('keyLabel')}</Label>
        <Input
          id='license_key'
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder={t('keyPlaceholder')}
          className='font-mono text-xs'
          disabled={activate.isPending}
        />
        <p className='text-muted-foreground text-xs'>{t('keyHelp')}</p>
      </div>

      <div className='flex items-center justify-end'>
        <Button
          size='sm'
          onClick={handleActivate}
          disabled={activate.isPending || key.trim().length === 0}
        >
          {activate.isPending ? (
            <Loader2 className='me-2 h-4 w-4 animate-spin' />
          ) : (
            <KeyRound className='me-2 h-4 w-4' />
          )}
          {t('activate')}
        </Button>
      </div>
    </div>
  );
}
